import React, { useImperativeHandle, useState, useMemo } from 'react';
import { Button, Image } from 'antd'; 
import { DeleteOutlined, FileOutlined } from '@ant-design/icons';
import { MISSING_IMAGE } from '../../consts';
import useCreateImageAsset from '../../gql/hooks/useCreateImageAsset';
import { authService } from '../../../auth/authService';
import { getEnvironment } from '../../utils';
import './styles.less';

const FormImageUpload = ( { form, name, type = 'heroImage', modifiedCallback, ref } ) => {
    const [ selectedFile, setSelectedFile ] = useState();  
    const [ selectedImage, setSelectedImage ] = useState();
    const [ deletedImage, setDeletedImage ] = useState( false );
    const { createImageAsset } = useCreateImageAsset();
    const [ resetTrigger, setResetTrigger ] = useState( false );
    
    useImperativeHandle( ref, () => ( {
        reset() {
            setResetTrigger( !resetTrigger );
            setSelectedImage( undefined );
            setSelectedFile( undefined );
            setDeletedImage( false );
            document.getElementById( type ).value = '';
            form.setFieldsValue( { [ `${ type }IsModified` ]: false } ); 
            form.setFieldsValue( { [ `${ type }Modified` ]: undefined } ); 
            modifiedCallback();
        },
        save() {
            return saveImage();
        }
    } ) );

    const saveImage = async () => {
        if ( selectedFile ) {
            const result = await createImageAsset( selectedFile ); 
        
            if ( result ) {
                form.setFieldsValue( { [ `${ type }IsModified` ]: true } ); 
                form.setFieldsValue( { [ `${ type }Modified` ]: {
                    id: result.id,
                    name: result.name,
                    url: result.url 
                } } ); 
            } else {
                form.setFieldsValue( { [ `${ type }IsModified` ]: false } ); 
                form.setFieldsValue( { [ `${ type }Modified` ]: undefined } );
            }
        } else {
            // Handle deleted image
            if ( deletedImage ) {
                form.setFieldsValue( { [ `${ type }IsModified` ]: true } ); 
                form.setFieldsValue( { [ `${ type }Modified` ]: undefined } ); 
            }
        }
    };

    const originalImage = useMemo( () => {
        const result = form.getFieldValue( type );

        if ( !result || result === null ) {
            return undefined;
        } else {
            return result;
        }
    }, [ form, type, resetTrigger ] );

    const displayImage = useMemo( () => { 
        if ( selectedImage ) {
            return selectedImage;
        }

        if ( deletedImage ) {
            return '';
        }

        if ( originalImage?.url ) {

            // Check for Hoya images at a legacy URL
            // Test with this course in QA: 
            // f079d7a1-7a56-46c7-ad2d-88134d1b4a5d/courses/edit/cfc1e113-0bc6-4f1b-b2dd-eef7320e1ed0
            if (  authService.isHoya() ) {
                if ( originalImage.url.startsWith( 'http' ) ) {
                    return originalImage.url;
                } else {
                    const env = getEnvironment();
 
                    if ( env === 'qa' ) {
                        return `https://qa.biwlearning-hoya.com${ originalImage.url }`;
                    } else if ( env === 'pprd' ) {
                        return `https://pprd.biwlearning-hoya.com${ originalImage.url }`;
                    } else {
                        return `https://prod.biwlearning-hoya.com${ originalImage.url }`;
                    }
                }
            } else {
                return originalImage.url;
            }
        }

        return '';
    }, [ selectedImage, originalImage, deletedImage ] );

    const hasImage = useMemo( () => {
        if ( displayImage ) {
            return true;
        } else {
            return false;
        }
    }, [ displayImage ] );

    const handleUploadClick = () => {
        window.document.getElementById( type ).click();
    };
    
    const handleDeleteClick = () => {
        if ( selectedImage ) {
            setSelectedImage( undefined );
            setSelectedFile( undefined );
            setDeletedImage( false );
            document.getElementById( type ).value = '';
            form.setFieldsValue( { [ `${ type }IsModified` ]: false } ); 
            form.setFieldsValue( { [ `${ type }Modified` ]: undefined } ); 
            modifiedCallback();
        } else {
            setDeletedImage( true );
            form.setFieldsValue( { [ `${ type }IsModified` ]: true } ); 
            form.setFieldsValue( { [ `${ type }Modified` ]: undefined } ); 
            modifiedCallback();
        }
    };
    
    const handleUploadFile = ( e ) => {
        const reader = new FileReader();
        const file = e.target.files[ 0 ];
        reader.readAsDataURL( file ); 

        reader.onload = readerEvent => {
            const content = readerEvent.target.result; 
            form.setFieldsValue( { [ `${ type }IsModified` ]: true } ); 
            form.setFieldsValue( { [ `${ type }Modified` ]: undefined } ); 
            setDeletedImage( false );
            modifiedCallback();
            setSelectedImage( content );
            setSelectedFile( file );
        };
    };

    /*************************************
     *  ANCHOR Render
     *************************************/

    return (
        <div className="form-item-image-upload">
            <h4>{ name }</h4>
            <div className="image-parent">
                <Image  
                    fallback={ MISSING_IMAGE }
                    src={ displayImage || MISSING_IMAGE }
                />
            </div>

            <div className="flex-container-buttons">
                <div className="left">
                     
                    <Button
                        type="link"  
                        disabled={ !hasImage }
                        icon={ 
                            <DeleteOutlined  style={ { color: !hasImage ? '#c0c0c0' : '#ff4d4f' } }/>
                        }
                        onClick={ handleDeleteClick }
                    />
                </div>

                <div className="right">
                    <input 
                        id={ type }
                        type="file" 
                        accept=".jpg,.png"
                        style={ { display: 'none' } }
                        onChange={ handleUploadFile }
                    />
                    
                    <Button 
                        icon={
                            <FileOutlined />    
                        }
                        onClick={ handleUploadClick }
                    >
                        Choose
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FormImageUpload;
